<template>
  <el-dialog
    :visible.sync="visible"
    :title="$t('message.complianceBlockTitle')"
    width="760px"
    append-to-body
    :show-close="false"
    :close-on-click-modal="false"
    custom-class="compliance-block-dialog"
  >
    <div class="compliance-block-dialog__warning-tip">
      <div v-if="overallRiskLevel !== null" class="compliance-block-dialog__risk-level">
        {{ $t("message.complianceRiskLevel", { level: overallRiskLevel }) }}
      </div>
      {{ blockMessage }}
    </div>

    <div v-if="reviewNotice" class="compliance-block-dialog__review-tip">
      {{ reviewNotice }}
    </div>

    <div v-if="hitDetails.length" class="compliance-block-dialog__risk-section">
      <div class="compliance-block-dialog__risk-title">
        {{ $t("message.complianceRiskDetails") }}
      </div>
      <div class="compliance-block-dialog__risk-list">
        <div
          v-for="(item, index) in hitDetails"
          :key="`${item}-${index}`"
          class="compliance-block-dialog__risk-item"
        >
          <div class="compliance-block-dialog__risk-item-content">
            {{ item }}
          </div>
        </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button :disabled="loading" @click="visible = false">{{
        $t("message.complianceClose")
      }}</el-button>
      <el-button
        v-if="!existingReview"
        type="primary"
        :loading="loading"
        :disabled="reviewCreated"
        @click="createReview"
      >
        {{ $t("message.complianceConfirm") }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { createComplianceReview } from "../../api/compliance";
import {
  complianceEventBus,
  getComplianceIdentifiers
} from "../../utils/compliance";

export default {
  name: "ComplianceBlockDialog",
  data() {
    return {
      visible: false,
      blockedResult: {
        message: ""
      },
      reviewResult: null,
      loading: false
    };
  },
  computed: {
    reviewCreated() {
      return !!this.reviewResult;
    },
    response() {
      return this.blockedResult && this.blockedResult.response
        ? this.blockedResult.response
        : null;
    },
    responseData() {
      return this.response && this.response.data ? this.response.data : null;
    },
    overallRiskLevel() {
      // 总体风险等级：来自合规平台整体评估（阻断阈值由后端 compliance.block-risk-level 配置决定）
      const data = this.responseData;
      return data && data.overallRiskLevel !== null && data.overallRiskLevel !== undefined
        ? data.overallRiskLevel
        : null;
    },
    blockMessage() {
      const data = this.responseData;
      return (
        (data && data.resultMsg) ||
        (this.blockedResult && this.blockedResult.message) ||
        (this.response && this.response.msg) ||
        this.$t("message.complianceFallback")
      );
    },
    hitDetails() {
      // 平台返回的命中明细 detailMsg：多个以逗号分隔，前端转数组逐条展示
      const detailMsg = this.responseData && this.responseData.detailMsg;
      if (!detailMsg) {
        return [];
      }
      return String(detailMsg)
        .split(",")
        .map(item => item.trim())
        .filter(item => item.length > 0);
    },
    existingReview() {
      // 该检测此前已创建的审查单（3 天内重复检测且已建单）：弹窗只保留「关闭」
      return (this.blockedResult && this.blockedResult.complianceReview) || null;
    },
    reviewNotice() {
      const info = this.existingReview;
      if (!info) {
        return "";
      }
      const rejected =
        String(info.handleStatus || "").toUpperCase() === "HANDLED" &&
        Number(info.handleResult) === 2;
      return rejected
        ? this.$t("message.complianceReviewRejected", { no: info.interceptNo || "-" })
        : this.$t("message.complianceReviewPending", { no: info.interceptNo || "-" });
    }
  },
  created() {
    complianceEventBus.$on("compliance-block", this.open);
  },
  beforeDestroy() {
    complianceEventBus.$off("compliance-block", this.open);
  },
  methods: {
    open(blockedResult) {
      this.blockedResult = blockedResult || {};
      this.reviewResult = null;
      this.visible = true;
    },
    async createReview() {
      const identifiers = getComplianceIdentifiers(this.blockedResult);
      if (!identifiers.detectionId && !identifiers.engineLogId) {
        this.$message.error(this.$t("message.complianceAppealMissing"));
        return;
      }

      this.loading = true;
      try {
        const response = await createComplianceReview({
          detectionId: identifiers.detectionId || null,
          engineLogId: identifiers.engineLogId || null,
          originalBizId: identifiers.originalBizId || null,
          initiateSummary: null,
          callbackUrl: null
        });
        if (response && response.status === "200") {
          this.reviewResult = response.data;
          const reviewNo =
            response.data &&
            (response.data.interceptNo || response.data.reviewId);
          const promptMessage = response.data && response.data.promptMessage;
          this.$message.success(
            promptMessage ||
              (reviewNo
                ? `${this.$t("message.complianceAppealSuccessNo")}${reviewNo}`
                : this.$t("message.complianceAppealSuccess"))
          );
          this.visible = false;
        } else {
          this.$message.error(
            (response && response.err) || this.$t("message.complianceAppealFailed")
          );
        }
      } catch (error) {
        this.$message.error(
          (error && error.message) || this.$t("message.complianceAppealFailed")
        );
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.compliance-block-dialog__risk-section {
  margin-top: 16px;
}

.compliance-block-dialog__risk-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 260px;
  padding-right: 4px;
  overflow-y: auto;
}

.compliance-block-dialog__risk-title {
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.compliance-block-dialog__risk-item {
  padding: 12px;
  background: #fff7f6;
  border: 1px solid #ffccc7;
  border-radius: 6px;
}

.compliance-block-dialog__risk-item-content {
  color: #cf1322;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  word-break: break-all;
}

.compliance-block-dialog__risk-level {
  margin-bottom: 4px;
  color: #cf1322;
  font-weight: 600;
}

.compliance-block-dialog__review-tip {
  margin-top: 8px;
  padding: 10px 12px;
  color: #1d6fd1;
  font-size: 13px;
  line-height: 20px;
  background: #e8f1ff;
  border: 1px solid #b3d4ff;
  border-radius: 6px;
}

.compliance-block-dialog__warning-tip {
  padding: 10px 12px;
  color: #ad6800;
  font-size: 13px;
  line-height: 20px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 6px;
}
</style>



// WEBPACK FOOTER //
// src/views/components/ComplianceBlockDialog.vue